import {
    committedTargetFeedback,
    type TargetLimits,
} from '@/dashboards/BudgetOptimization/core/budgetSessionRules';
import type { OptimisationRun } from '@/dashboards/shared/optimisation/optimisationSession';
import type { OptimisationBase } from '@/optimizer';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { useEffect, useRef, useState } from 'react';

// Recommended range and optimiser warning for the target input of `base`. Taken from the
// committed run when it was optimised for the same base; otherwise the run's limits are in
// other units, so ask the optimiser for this base's own range instead.
export const useTargetLimits = (
    base: OptimisationBase,
    committed: OptimisationRun | null,
): { limits: TargetLimits | null; budgetError: string | undefined } => {
    const { optimizer, optimizerReady } = useOptimizer();
    const feedback = committedTargetFeedback(committed, base);
    const needsFetch = feedback.limits === null && committed !== null;

    const [fetched, setFetched] = useState<{
        key: string;
        limits: TargetLimits | null;
    } | null>(null);
    const requestRef = useRef(0);
    const fetchKey = `${base}:${committed?.finishedAt ?? ''}`;

    useEffect(() => {
        if (!needsFetch || !optimizerReady) return;
        const request = ++requestRef.current;
        const getLimits =
            base === 'media_kpi'
                ? optimizer.getKPILimits
                : base === 'total_kpi'
                  ? optimizer.getTotalKPILimits
                  : optimizer.getBudgetLimits;

        getLimits()
            .then((limits) => {
                if (request !== requestRef.current) return;
                setFetched({
                    key: fetchKey,
                    limits:
                        limits && (limits.lower > 0 || limits.upper > 0)
                            ? { min: limits.lower, max: limits.upper }
                            : null,
                });
            })
            .catch((error) => {
                if (request !== requestRef.current) return;
                // eslint-disable-next-line no-console
                console.error('Failed to fetch target limits:', error);
                setFetched({ key: fetchKey, limits: null });
            });
    }, [needsFetch, optimizerReady, optimizer, base, fetchKey]);

    if (!needsFetch) return feedback;
    return {
        limits: fetched?.key === fetchKey ? fetched.limits : null,
        budgetError: undefined,
    };
};
